import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AlertController, ModalController, NavController, ToastController } from '@ionic/angular';
import { RemindersComponent } from 'app/components/shared/reminders/reminders.component';
import { TaskModel, TasksSearchResult } from 'app/models/dms/tasksSearchResult.model';
import { LookupExternalModel, LookupInternalModel } from 'app/models/shared/lookup.model';
import { Constants } from 'app/utility/constants';
import { DynamicReportTaskStateEnum, LookupExternalCollection, TaskStatus } from 'app/utility/enums';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { CacheService } from './cache.service';
import { FieldGroupModel } from 'app/models/shared/fieldGroup.model';
import { MenuService } from './menu.service';
import { StorageService } from './storage.service';
import { Router } from '@angular/router';
import { ReportParamSetModel } from 'app/models/dms/reportSpec.model';

@Injectable({
    providedIn: 'root'
})
export class TaskService {

    constructor(
        private httpClient: HttpClient,
        public toastController: ToastController,
        public modalController: ModalController,
        private cache: CacheService,
        private menuService: MenuService,
        private storageService: StorageService,
        private navCtrl: NavController,
        private alertController: AlertController,
        private router: Router

    ) {
        this.restoreDynamicReportState();
        this.restoreBulkUpdateState();
        this.restoreAccountingExportState();
    }

    getTasks(
        taskTypeId: string = Constants.EmptyGuid, taskStatusId: string = Constants.EmptyGuid, query: string = '',
        startDate: string = Constants.DateMinimum, endDate: string = Constants.DateMaximum, pageNumber: number = 1
    ): Observable<TasksSearchResult> {
        let httpParams = new HttpParams();
        httpParams = httpParams.append('taskTypeId', taskTypeId);
        httpParams = httpParams.append('taskStatusId', taskStatusId);
        httpParams = httpParams.append('query', query);
        httpParams = httpParams.append('startDate', startDate);
        httpParams = httpParams.append('endDate', endDate);
        httpParams = httpParams.append('pageNumber', pageNumber.toString());
        return this.httpClient.get<TasksSearchResult>(Constants.ApiUrl + 'Tasks/tasks', { params: httpParams });
    }

    selectTask(taskId: string): Observable<TaskModel> {
        return this.httpClient.get<TaskModel>(Constants.ApiUrl + 'Tasks/' + taskId);
    }

    getTaskReminderTimes(): Observable<LookupInternalModel[]> {
        return this.httpClient.get<LookupInternalModel[]>(Constants.ApiUrl + 'Tasks/TaskReminderTimes');
    }

    getCachedTaskReminderTimes(): Observable<LookupInternalModel[]> {
        let cacheKey: string = 'TaskReminderTimes';
        let cacheMilliseconds: number = 1 * 60 * 60000 * 24; //24 hour
        return this.cache.get(cacheKey, this.getTaskReminderTimes(), cacheMilliseconds);
    }

    getTaskTemplate(): Observable<TaskModel> {
        return this.httpClient.get<TaskModel>(Constants.ApiUrl + 'Tasks/new');
    }

    createTask(taskId: string, task: TaskModel): Observable<TaskModel> {
        return this.httpClient.post<TaskModel>(Constants.ApiUrl + 'Tasks/' + taskId + '/new', task);
    }

    updateTask(taskId: string, task: TaskModel): Observable<TaskModel> {
        return this.httpClient.patch<TaskModel>(Constants.ApiUrl + 'Tasks/' + taskId, task);
    }

    cancelTask(taskId: string): Observable<TaskModel> {
        return this.httpClient.patch<TaskModel>(Constants.ApiUrl + 'Tasks/' + taskId + '/cancel', taskId);
    }

    completeTask(taskId: string, withPrompts: boolean): Observable<TaskModel> {
        let httpParams = new HttpParams();
        httpParams = httpParams.append('withPrompts', withPrompts);
        return this.httpClient.patch<TaskModel>(Constants.ApiUrl + 'Tasks/' + taskId + '/complete', httpParams);
    }

    deleteTask(taskId: string): Observable<boolean> {
        return this.httpClient.delete<boolean>(Constants.ApiUrl + 'Tasks/' + taskId);
    }


    public getReminders(): Observable<TasksSearchResult> {
        return this.httpClient.get<TasksSearchResult>(Constants.ApiUrl + 'Tasks/reminders');
    }

    public async fetchReminders(showToast: boolean = false) {
        this.getReminders().pipe(
            timeout(Constants.ApiShortTimeout))
            .subscribe({
                next: newReminders => {
                    if (!this.reminders || this.reminders.resultCount !== newReminders.resultCount) {
                        this.remindersSubject.next(newReminders);
                    }

                    if (!this.reminders || JSON.stringify(this.reminders) !== JSON.stringify(newReminders)) {
                        this.reminders = newReminders;
                        if (showToast && this.reminders.resultCount > 0) {
                            this.showRemindersToast();
                        }
                    }
                },
                error: err => {
                    // console.error(err);
                }
            });
    }

    private reminders: TasksSearchResult;
    private isRemindersComponentOpen: boolean = false;
    //call this from the tabs service
    async doGetRemindersLoop() {
        await this.fetchReminders(true);
        setTimeout(() => {
            this.doGetRemindersLoop()
        }, 30 * 60 * 1000); // 30 minutes
    }

    public remindersSubject: BehaviorSubject<TasksSearchResult> = new BehaviorSubject<TasksSearchResult>(null);

    async showRemindersToast() {
        if (this.isRemindersComponentOpen) {
            return;
        }
        const toast = await this.toastController.create({
            message: "You have reminders to view or action",
            position: 'bottom',
            color: 'dark',
            duration: 20000,
            animated: true,
            buttons: [
                {
                    text: 'View',
                    handler: async () => {
                        const modal = await this.modalController.create({
                            component: RemindersComponent,
                            componentProps: { taskService: this }

                        });
                        toast.dismiss();
                        modal.present();
                    }
                },
                {
                    side: 'end',
                    icon: 'close-circle-outline',
                    role: 'cancel',
                    handler: () => {
                        toast.dismiss();
                    }
                }
            ]
        });
        toast.present();
    }

    setRemindersComponentOpen(isOpen: boolean) {
        this.isRemindersComponentOpen = isOpen;
        if (isOpen) {
            setTimeout(() => {
                this.isRemindersComponentOpen = false;
            }, 300000);
        }
    }

    snoozeReminder(taskId: string, snoozeSeconds: number): Observable<boolean> {
        let httpParams = new HttpParams();
        httpParams = httpParams.append('taskId', taskId);
        httpParams = httpParams.append('snoozeSeconds', snoozeSeconds.toString());
        return this.httpClient.patch<boolean>(Constants.ApiUrl + 'Tasks/' + taskId + '/snoozeReminder', httpParams);
    }

    getTaskFilters(): Observable<Array<LookupExternalModel[]>> {
        return this.httpClient.get<any>(Constants.ApiUrl + 'Tasks/taskFilters');
    }

    dismissAllTaskReminders(): Observable<boolean> {
        let httpParams = new HttpParams();
        return this.httpClient.patch<boolean>(Constants.ApiUrl + 'Tasks/dismissAllTaskReminders', httpParams);
    }

    public syncFormWithObject(data: any, fieldGroups: FieldGroupModel[]): any {
        var data_changed = false;

        for (let field_group of fieldGroups) {
            for (let field of field_group.fields) {
                // console.log(data[field.id]);
                if (data[field.id] != null && data[field.id] != field.value && !field.invalid && field.dirty) {
                    data_changed = true;
                    data[field.id] = field.value;
                }
            }
        }
        return data;
    }

    private dynamicReportStateSubject = new BehaviorSubject<DynamicReportTaskState>(null);
    public dynamicReportState$ = this.dynamicReportStateSubject.asObservable();

    private reportStatusTimeout: any = null;

    private readonly REPORT_TASK_STATE_KEY = 'DynamicReportTaskState';
    private readonly REPORT_TASK_ID_KEY = Constants.IonicStorage_DynamicReportScheduledTaskId;

    private persistDynamicReportState(state: DynamicReportTaskState)
    {
        this.dynamicReportStateSubject.next(state);
        this.storageService.set(this.REPORT_TASK_STATE_KEY, state);

        if (state?.status === DynamicReportTaskStateEnum.Pending)
        {
            this.menuService.setMenuItemSpinner('/reports', true);
            this.menuService.setMenuItemStatusIcon('/reports', null);
        }
        else if (state?.status === DynamicReportTaskStateEnum.Completed && !state.viewed)
        {
            this.menuService.setMenuItemSpinner('/reports', false);
            this.menuService.setMenuItemStatusIcon('/reports', 'checkmark-circle', 'primary');
        }
        else if (state?.status === DynamicReportTaskStateEnum.Error)
        {
            this.menuService.setMenuItemSpinner('/reports', false);
            this.menuService.setMenuItemStatusIcon('/reports', 'alert-circle', 'danger');
        }
        else
        {
            this.menuService.clearMenuItemState('/reports');
        }
    }

    private async restoreDynamicReportState()
    {
        const saved: DynamicReportTaskState = await this.storageService.get(this.REPORT_TASK_STATE_KEY);
        if (saved && saved.taskId)
        {
            this.dynamicReportStateSubject.next(saved);
            if (saved.status === DynamicReportTaskStateEnum.Pending)
            {
                this.menuService.setMenuItemSpinner('/reports', true);
                this.pollDynamicReportStatus(saved.taskId);
            }
            else if (saved.status === DynamicReportTaskStateEnum.Completed && !saved.viewed)
            {
                this.menuService.setMenuItemStatusIcon('/reports', 'checkmark-circle', 'primary');
            }
        }
    }

    private async clearDynamicReportStateStorage()
    {
        await this.storageService.remove(this.REPORT_TASK_STATE_KEY, true);
        await this.storageService.remove(this.REPORT_TASK_ID_KEY, true);
    }

    public startDynamicReportGeneration(taskId: string, reportName?: string)
    {
        const state: DynamicReportTaskState = {taskId, status: DynamicReportTaskStateEnum.Pending, reportName, startedAt: new Date().toISOString(), viewed: false};
        this.storageService.set(this.REPORT_TASK_ID_KEY, taskId);
        this.persistDynamicReportState(state);
        this.pollDynamicReportStatus(taskId);
    }

    public cancelDynamicReportTask()
    {
        if (this.reportStatusTimeout)
        {
            clearTimeout(this.reportStatusTimeout);
            this.reportStatusTimeout = null;
        }
        this.clearDynamicReportStateStorage();
        this.persistDynamicReportState(null);
    }

    public markDynamicReportViewed()
    {
        const current = this.dynamicReportStateSubject.value;
        if (current) {
            current.viewed = true;
            this.persistDynamicReportState({ ...current });
        }
    }

    private readonly MAX_TASK_AGE_MS = 30 * 60 * 1000;
    private readonly MAX_RETRY = 5;
    failedTaskSelectCount: number = 0;
    failedBulkUpdateTaskSelectCount: number = 0;

    private pollDynamicReportStatus(taskId: string)
    {
        if (!taskId) return;

        this.selectTask(taskId).subscribe({
            next: task => {
                this.failedTaskSelectCount = 0;
                
                const createdRaw = task.createdDate?.toString();

                let createdDate: Date = null;

                if (createdRaw)
                {
                    createdDate = createdRaw.endsWith('Z') ? new Date(createdRaw.slice(0, -1)) : new Date(createdRaw);
                }

                let reportName: string = this.dynamicReportStateSubject.value?.reportName;
                let reportParamsString: string = "";
                if (task.comment)
                {
                    try
                    {
                        const payload = JSON.parse(task.comment);
                        reportName = payload['ReportName'] || reportName;
                        reportParamsString = payload['ReportParamsString'] || "";
                    }
                    catch
                    {

                    }
                }

                const previous = this.dynamicReportStateSubject.value;

                if (task.taskStatus.member === TaskStatus.Pending)
                {
                    if (createdDate)
                    {
                        const ageMs = Date.now() - createdDate.getTime();
                        if (ageMs > this.MAX_TASK_AGE_MS)
                        {
                            this.persistDynamicReportState({ taskId, status: DynamicReportTaskStateEnum.Error, reportName, reportParamsString, startedAt: createdDate?.toISOString(), error: 'Timed out', viewed: false });
                            this.menuService.clearMenuItemState('/reports');
                            return;
                        }
                    }

                    this.persistDynamicReportState({ taskId, status: DynamicReportTaskStateEnum.Pending, reportName, reportParamsString, startedAt: createdDate?.toISOString(), viewed: false, notified: previous?.notified });
                    this.reportStatusTimeout = setTimeout(() => {
                        this.pollDynamicReportStatus(taskId)
                    }, 5000);
                }
                else if (task.taskStatus.member === TaskStatus.Completed && task.reference)
                {
                    const firstCompletion = previous?.status !== DynamicReportTaskStateEnum.Completed;
                    const onReports = this.isOnReportsPage();
                    this.persistDynamicReportState({ taskId, status: DynamicReportTaskStateEnum.Completed, reportName, reportParamsString, startedAt: createdDate?.toISOString(), completedAt: new Date().toISOString(), reference: task.reference, viewed: false, notified: !onReports, reportParamSets: task.reportParamsPayload?.reportParams, reportSpecId: task.reportParamsPayload?.reportSpecId });
                    if (firstCompletion && !onReports) {
                        // show toast once (off the reports page)
                        this.showReportReadyToast();
                    }
                }
                else if (task.taskStatus.member === TaskStatus.Cancelled)
                {
                    this.persistDynamicReportState({ taskId, status: DynamicReportTaskStateEnum.Cancelled, reportName, reportParamsString, startedAt: createdDate?.toISOString(), reference: task.reference, viewed: false, notified: previous?.notified });
                    this.menuService.clearMenuItemState('/reports');
                }
                else
                {
                    this.persistDynamicReportState({ taskId, status: DynamicReportTaskStateEnum.Error, reportName, reportParamsString, startedAt: createdDate?.toISOString(), error: 'Failed', viewed: false, notified: previous?.notified });
                    this.showReportFailedToast();
                }
            },
            error: _ =>
            {
                this.failedTaskSelectCount++;
                if (this.failedTaskSelectCount >= this.MAX_RETRY)
                {
                    this.persistDynamicReportState({ taskId, status: DynamicReportTaskStateEnum.Error, reportName: this.dynamicReportStateSubject.value?.reportName, reportParamsString: this.dynamicReportStateSubject.value?.reportParamsString, error: 'Failed to retrieve task status', viewed: false });
                    this.showReportFailedToast();
                    return;
                }

                // retry quietly
                this.reportStatusTimeout = setTimeout(() =>  {
                    this.pollDynamicReportStatus(taskId)
                }, 7000);
            }
        });
    }

    public getCurrentDynamicReportState(): DynamicReportTaskState
    {
        return this.dynamicReportStateSubject.value;
    }

    async showReportReadyToast()
    {

        const toast = await this.toastController.create({
            message: "Report has been generated",
            position: 'bottom',
            color: 'dark',
            duration: 20000,
            animated: true,
            buttons: [
                {
                    text: 'View now',
                    handler: async () => {
                        this.navCtrl.navigateForward('/reports');
                        toast.dismiss();
                    }
                },
                {
                    side: 'end',
                    icon: 'close-circle-outline',
                    role: 'cancel',
                    handler: () => {
                        toast.dismiss();
                    }
                }
            ]
        });
        toast.present();
    }

    async showReportFailedToast()
    {

        const toast = await this.toastController.create({
            message: "Report has failed to generate",
            position: 'bottom',
            color: 'danger',
            duration: 20000,
            animated: true,
            buttons: [
                {
                    text: 'View Reports',
                    handler: async () => {
                        this.navCtrl.navigateForward('/reports');
                        toast.dismiss();
                    }
                },
                {
                    side: 'end',
                    icon: 'close-circle-outline',
                    role: 'cancel',
                    handler: () => {
                        toast.dismiss();
                    }
                }
            ]
        });
        toast.present();
    }

    private isOnReportsPage(): boolean
    {
        return this.router?.url?.startsWith('/reports');
    }


    // -----------------------------
    // Bulk update task polling
    // -----------------------------

    private bulkUpdateStateSubject = new BehaviorSubject<BulkUpdateTaskState>(null);
    public bulkUpdateState$ = this.bulkUpdateStateSubject.asObservable();

    private bulkUpdateStatusTimeout: any = null;

    private readonly BULK_UPDATE_TASK_STATE_KEY = 'BulkUpdateTaskState';
    private readonly BULK_UPDATE_TASK_ID_KEY = Constants.IonicStorage_BulkUpdateScheduledTaskId;
    private readonly BULK_UPDATE_MENU_URL = '/tools/bulk-update';

    private persistBulkUpdateState(state: BulkUpdateTaskState)
    {
        this.bulkUpdateStateSubject.next(state);
        this.storageService.set(this.BULK_UPDATE_TASK_STATE_KEY, state);

        if (state?.status === DynamicReportTaskStateEnum.Pending)
        {
            this.menuService.setMenuItemSpinner(this.BULK_UPDATE_MENU_URL, true);
            this.menuService.setMenuItemStatusIcon(this.BULK_UPDATE_MENU_URL, null);
        }
        else if (state?.status === DynamicReportTaskStateEnum.Completed && !state.viewed)
        {
            this.menuService.setMenuItemSpinner(this.BULK_UPDATE_MENU_URL, false);
            this.menuService.setMenuItemStatusIcon(this.BULK_UPDATE_MENU_URL, 'checkmark-circle', 'primary');
        }
        else if (state?.status === DynamicReportTaskStateEnum.Error)
        {
            this.menuService.setMenuItemSpinner(this.BULK_UPDATE_MENU_URL, false);
            this.menuService.setMenuItemStatusIcon(this.BULK_UPDATE_MENU_URL, 'alert-circle', 'danger');
        }
        else
        {
            this.menuService.clearMenuItemState(this.BULK_UPDATE_MENU_URL);
        }
    }

    private async restoreBulkUpdateState()
    {
        const saved: BulkUpdateTaskState = await this.storageService.get(this.BULK_UPDATE_TASK_STATE_KEY);
        if (saved && saved.taskId)
        {
            this.bulkUpdateStateSubject.next(saved);
            if (saved.status === DynamicReportTaskStateEnum.Pending)
            {
                this.menuService.setMenuItemSpinner(this.BULK_UPDATE_MENU_URL, true);
                this.pollBulkUpdateStatus(saved.taskId);
            }
            else if (saved.status === DynamicReportTaskStateEnum.Completed && !saved.viewed)
            {
                this.menuService.setMenuItemStatusIcon(this.BULK_UPDATE_MENU_URL, 'checkmark-circle', 'primary');
            }
        }
    }

    private async clearBulkUpdateStateStorage()
    {
        await this.storageService.remove(this.BULK_UPDATE_TASK_STATE_KEY, true);
        await this.storageService.remove(this.BULK_UPDATE_TASK_ID_KEY, true);
    }

    public startBulkUpdateTask(taskId: string, description?: string)
    {
        const state: BulkUpdateTaskState = {
            taskId,
            status: DynamicReportTaskStateEnum.Pending,
            startedAt: new Date().toISOString(),
            viewed: false,
            description
        };

        this.storageService.set(this.BULK_UPDATE_TASK_ID_KEY, taskId);
        this.persistBulkUpdateState(state);
        this.pollBulkUpdateStatus(taskId);
    }

    public cancelBulkUpdateTask()
    {
        if (this.bulkUpdateStatusTimeout)
        {
            clearTimeout(this.bulkUpdateStatusTimeout);
            this.bulkUpdateStatusTimeout = null;
        }
        this.clearBulkUpdateStateStorage();
        this.persistBulkUpdateState(null);
    }

    public markBulkUpdateViewed()
    {
        const current = this.bulkUpdateStateSubject.value;
        if (current)
        {
            current.viewed = true;
            this.persistBulkUpdateState({ ...current });
        }
    }

    public getCurrentBulkUpdateState(): BulkUpdateTaskState
    {
        return this.bulkUpdateStateSubject.value;
    }

    private pollBulkUpdateStatus(taskId: string)
    {
        if (!taskId) return;

        this.selectTask(taskId).subscribe({
            next: task =>
            {
                this.failedBulkUpdateTaskSelectCount = 0;
                const createdRaw = task.createdDate?.toString();
                let createdDate: Date = null;
                if (createdRaw)
                {
                    createdDate = createdRaw.endsWith('Z') ? new Date(createdRaw.slice(0, -1)) : new Date(createdRaw);
                }

                const previous = this.bulkUpdateStateSubject.value;
                const description = previous?.description;

                if (task.taskStatus.member === TaskStatus.Pending)
                {
                    if (createdDate)
                    {
                        const ageMs = Date.now() - createdDate.getTime();
                        if (ageMs > this.MAX_TASK_AGE_MS)
                        {
                            this.persistBulkUpdateState({ taskId, status: DynamicReportTaskStateEnum.Error, startedAt: createdDate?.toISOString(), error: 'Timed out', viewed: false, description });
                            this.menuService.clearMenuItemState(this.BULK_UPDATE_MENU_URL);
                            return;
                        }
                    }

                    this.persistBulkUpdateState({ taskId, status: DynamicReportTaskStateEnum.Pending, startedAt: createdDate?.toISOString(), viewed: false, description, notified: previous?.notified });
                    this.bulkUpdateStatusTimeout = setTimeout(() =>
                    {
                        this.pollBulkUpdateStatus(taskId);
                    }, 5000);
                }
                else if (task.taskStatus.member === TaskStatus.Completed)
                {
                    const firstCompletion = previous?.status !== DynamicReportTaskStateEnum.Completed;
                    const onBulkUpdate = this.isOnBulkUpdatePage();

                    this.persistBulkUpdateState({ taskId, status: DynamicReportTaskStateEnum.Completed, startedAt: createdDate?.toISOString(), completedAt: new Date().toISOString(), viewed: false, description, notified: !onBulkUpdate });

                    if (firstCompletion && !onBulkUpdate)
                    {
                        this.showBulkUpdateReadyToast();
                    }
                }
                else if (task.taskStatus.member === TaskStatus.Cancelled)
                {
                    this.persistBulkUpdateState({ taskId, status: DynamicReportTaskStateEnum.Cancelled, startedAt: createdDate?.toISOString(), viewed: false, description, notified: previous?.notified });
                    this.menuService.clearMenuItemState(this.BULK_UPDATE_MENU_URL);
                }
                else
                {
                    this.persistBulkUpdateState({ taskId, status: DynamicReportTaskStateEnum.Error, startedAt: createdDate?.toISOString(), error: 'Failed', viewed: false, description, notified: previous?.notified });
                    this.showBulkUpdateFailedToast();
                }
            },
            error: _ =>
            {
                // Reuse the same retry strategy as report polling
                this.failedBulkUpdateTaskSelectCount++;
                if (this.failedBulkUpdateTaskSelectCount >= this.MAX_RETRY)
                {
                    this.persistBulkUpdateState({ taskId, status: DynamicReportTaskStateEnum.Error, error: 'Failed to retrieve task status', viewed: false, description: this.bulkUpdateStateSubject.value?.description });
                    this.showBulkUpdateFailedToast();
                    return;
                }

                this.bulkUpdateStatusTimeout = setTimeout(() =>
                {
                    this.pollBulkUpdateStatus(taskId);
                }, 7000);
            }
        });
    }

    async showBulkUpdateReadyToast()
    {
        const toast = await this.toastController.create({
            message: 'Bulk update has completed',
            position: 'bottom',
            color: 'dark',
            duration: 20000,
            animated: true,
            buttons: [
                {
                    text: 'View Tasks',
                    handler: async () =>
                    {
                        this.navCtrl.navigateForward('/tools/tasks');
                        toast.dismiss();
                    }
                },
                {
                    side: 'end',
                    icon: 'close-circle-outline',
                    role: 'cancel',
                    handler: () =>
                    {
                        toast.dismiss();
                    }
                }
            ]
        });

        toast.present();
    }

    async showBulkUpdateFailedToast()
    {
        const toast = await this.toastController.create({
            message: 'Bulk update has failed',
            position: 'bottom',
            color: 'danger',
            duration: 20000,
            animated: true,
            buttons: [
                {
                    text: 'View Tasks',
                    handler: async () =>
                    {
                        this.navCtrl.navigateForward('/tools/tasks');
                        toast.dismiss();
                    }
                },
                {
                    side: 'end',
                    icon: 'close-circle-outline',
                    role: 'cancel',
                    handler: () =>
                    {
                        toast.dismiss();
                    }
                }
            ]
        });

        toast.present();
    }

    private isOnBulkUpdatePage(): boolean
    {
        return this.router?.url?.startsWith(this.BULK_UPDATE_MENU_URL);
    }

    private accountingExportStateSubject = new BehaviorSubject<AccountingExportTaskState>(null);
    public accountingExportState$ = this.accountingExportStateSubject.asObservable();

    private accountingExportStatusTimeout: any = null;
    private failedAccountingExportTaskSelectCount = 0;
    private readonly ACCOUNTING_EXPORT_TASK_STATE_KEY = 'AccountingExportTaskState';
    private readonly ACCOUNTING_EXPORT_TASK_ID_KEY = Constants.IonicStorage_AccountingExportScheduledTaskId;
    private readonly ACCOUNTING_EXPORT_MENU_URL = '/tools/run-export';

    private persistAccountingExportState(state: AccountingExportTaskState)
    {
        this.accountingExportStateSubject.next(state);
        this.storageService.set(this.ACCOUNTING_EXPORT_TASK_STATE_KEY, state);

        if (state?.status === DynamicReportTaskStateEnum.Pending)
        {
            this.menuService.setMenuItemSpinner(this.ACCOUNTING_EXPORT_MENU_URL, true);
            this.menuService.setMenuItemStatusIcon(this.ACCOUNTING_EXPORT_MENU_URL, null);
        }
        else if
        (state?.status === DynamicReportTaskStateEnum.Completed && !state.viewed)
        {
            this.menuService.setMenuItemSpinner(this.ACCOUNTING_EXPORT_MENU_URL, false);
            this.menuService.setMenuItemStatusIcon(this.ACCOUNTING_EXPORT_MENU_URL, 'checkmark-circle', 'primary');
        }
        else if
        (state?.status === DynamicReportTaskStateEnum.Error)
        {
            this.menuService.setMenuItemSpinner(this.ACCOUNTING_EXPORT_MENU_URL, false);
            this.menuService.setMenuItemStatusIcon(this.ACCOUNTING_EXPORT_MENU_URL, 'alert-circle', 'danger');
        }
        else
        {
            this.menuService.clearMenuItemState(this.ACCOUNTING_EXPORT_MENU_URL);
        }
    }

    private async restoreAccountingExportState()
    {
        const saved: AccountingExportTaskState = await this.storageService.get(this.ACCOUNTING_EXPORT_TASK_STATE_KEY);
        if (!saved?.taskId)
        {
            return;
        }

        this.accountingExportStateSubject.next(saved);
        if (saved.status === DynamicReportTaskStateEnum.Pending)
        {
            this.menuService.setMenuItemSpinner(this.ACCOUNTING_EXPORT_MENU_URL, true);
            this.pollAccountingExportStatus(saved.taskId);
        }
        else if
        (saved.status === DynamicReportTaskStateEnum.Completed && !saved.viewed)
        {
            this.menuService.setMenuItemStatusIcon(this.ACCOUNTING_EXPORT_MENU_URL, 'checkmark-circle', 'primary');
        }
    }

    public startAccountingExportTask(taskId: string)
    {
        if (!taskId)
        {
            throw new Error('The accounting export task did not return a task id.');
        }

        this.failedAccountingExportTaskSelectCount = 0;
        const state: AccountingExportTaskState = {
            taskId,
            status: DynamicReportTaskStateEnum.Pending,
            startedAt: new Date().toISOString(),
            viewed: false
        };
        this.storageService.set(this.ACCOUNTING_EXPORT_TASK_ID_KEY, taskId);
        this.persistAccountingExportState(state);
        this.pollAccountingExportStatus(taskId);
    }

    public markAccountingExportViewed()
    {
        const current = this.accountingExportStateSubject.value;
        if (current)
        {
            this.persistAccountingExportState({ ...current, viewed: true });
        }
    }

    public getCurrentAccountingExportState(): AccountingExportTaskState
    {
        return this.accountingExportStateSubject.value;
    }

    private pollAccountingExportStatus(taskId: string)
    {
        if (!taskId)
        {
            return;
        }

        this.selectTask(taskId).subscribe({
            next: task =>
            {
                if (!task?.taskStatus)
                {
                    this.handleAccountingExportStatusError(taskId);
                    return;
                }

                this.failedAccountingExportTaskSelectCount = 0;
                const previous = this.accountingExportStateSubject.value;
                const createdRaw = task.createdDate?.toString();
                const createdDate = createdRaw
                    ? new Date(createdRaw.endsWith('Z') ? createdRaw.slice(0, -1) : createdRaw)
                    : null;
                if (task.taskStatus.member === TaskStatus.Pending)
                {
                    if (createdDate && Date.now() - createdDate.getTime() > this.MAX_TASK_AGE_MS)
                    {
                        this.persistAccountingExportState({
                            taskId,
                            status: DynamicReportTaskStateEnum.Error,
                            startedAt: createdDate.toISOString(),
                            error: 'Timed out',
                            viewed: false
                        });
                        if (!this.isOnAccountingExportPage())
                        {
                            this.showAccountingExportFailedToast();
                        }
                        return;
                    }

                    this.persistAccountingExportState({
                        taskId,
                        status: DynamicReportTaskStateEnum.Pending,
                        startedAt: createdDate?.toISOString(),
                        viewed: false
                    });
                    this.accountingExportStatusTimeout = setTimeout(() =>
                    {
                        this.pollAccountingExportStatus(taskId);
                    }, 5000);
                }
                else if
                (task.taskStatus.member === TaskStatus.Completed)
                {
                    const onRunExport = this.isOnAccountingExportPage();
                    const firstCompletion = previous?.status !== DynamicReportTaskStateEnum.Completed;
                    this.persistAccountingExportState({
                        taskId,
                        status: DynamicReportTaskStateEnum.Completed,
                        startedAt: createdDate?.toISOString(),
                        completedAt: new Date().toISOString(),
                        reference: task.reference,
                        viewed: false,
                        notified: !onRunExport
                    });
                    if (firstCompletion && !onRunExport)
                    {
                        this.showAccountingExportReadyToast();
                    }
                }
                else if
                (task.taskStatus.member === TaskStatus.Cancelled)
                {
                    this.persistAccountingExportState({
                        taskId,
                        status: DynamicReportTaskStateEnum.Cancelled,
                        startedAt: createdDate?.toISOString(),
                        reference: task.reference,
                        viewed: false
                    });
                    if (previous?.status !== DynamicReportTaskStateEnum.Cancelled && !this.isOnAccountingExportPage())
                    {
                        this.showAccountingExportFailedToast();
                    }
                }
                else
                {
                    this.persistAccountingExportState({
                        taskId,
                        status: DynamicReportTaskStateEnum.Error,
                        startedAt: createdDate?.toISOString(),
                        error: 'Failed',
                        viewed: false
                    });
                    if (!this.isOnAccountingExportPage())
                    {
                        this.showAccountingExportFailedToast();
                    }
                }
            },
            error: _ =>
            {
                this.handleAccountingExportStatusError(taskId);
            }
        });
    }

    private handleAccountingExportStatusError(taskId: string)
    {
        this.failedAccountingExportTaskSelectCount++;
        if (this.failedAccountingExportTaskSelectCount >= this.MAX_RETRY)
        {
            const previous = this.accountingExportStateSubject.value;
            this.persistAccountingExportState({
                taskId,
                status: DynamicReportTaskStateEnum.Error,
                startedAt: previous?.startedAt,
                error: 'Failed to retrieve task status',
                viewed: false
            });
            if (!this.isOnAccountingExportPage())
            {
                this.showAccountingExportFailedToast();
            }
            return;
        }

        this.accountingExportStatusTimeout = setTimeout(() =>
        {
            this.pollAccountingExportStatus(taskId);
        }, 7000);
    }

    async showAccountingExportReadyToast()
    {
        const toast = await this.toastController.create({
            message: 'Accounting export has completed',
            position: 'bottom',
            color: 'dark',
            duration: 20000,
            animated: true,
            buttons: [
                {
                    text: 'View Export',
                    handler: async () =>
                    {
                        this.navCtrl.navigateForward(this.ACCOUNTING_EXPORT_MENU_URL);
                        toast.dismiss();
                    }
                },
                {
                    side: 'end',
                    icon: 'close-circle-outline',
                    role: 'cancel',
                    handler: () =>
                    {
                        toast.dismiss();
                    }
                }
            ]
        });
        toast.present();
    }

    async showAccountingExportFailedToast()
    {
        const toast = await this.toastController.create({
            message: 'Accounting export has failed',
            position: 'bottom',
            color: 'danger',
            duration: 20000,
            animated: true,
            buttons: [
                {
                    text: 'View Export',
                    handler: async () =>
                    {
                        this.navCtrl.navigateForward(this.ACCOUNTING_EXPORT_MENU_URL);
                        toast.dismiss();
                    }
                },
                {
                    side: 'end',
                    icon: 'close-circle-outline',
                    role: 'cancel',
                    handler: () =>
                    {
                        toast.dismiss();
                    }
                }
            ]
        });
        toast.present();
    }

    private isOnAccountingExportPage(): boolean
    {
        return this.router?.url?.startsWith(this.ACCOUNTING_EXPORT_MENU_URL);
    }
}

export interface DynamicReportTaskState
{
    taskId: string;
    status: DynamicReportTaskStateEnum
    reportName?: string;
    reportParamsString?: string;
    reference?: string;
    startedAt?: string;
    completedAt?: string;
    error?: string;
    viewed?: boolean;
    notified?: boolean;
    reportParamSets?: ReportParamSetModel[];
    reportSpecId?: string;
}

export interface BulkUpdateTaskState
{
    taskId: string;
    status: DynamicReportTaskStateEnum;
    description?: string;
    startedAt?: string;
    completedAt?: string;
    error?: string;
    viewed?: boolean;
    notified?: boolean;
}

export interface AccountingExportTaskState
{
    taskId: string;
    status: DynamicReportTaskStateEnum;
    startedAt?: string;
    completedAt?: string;
    reference?: string;
    error?: string;
    viewed?: boolean;
    notified?: boolean;
}
